import { useEffect, useMemo, useState } from "react";
import {
  type Draft,
  type Exercise,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { LoggerProfile } from "../../Logger/hostDeps";
import type {
  DraftEntry,
  LoggerDraft,
  WeightUnit,
} from "../../Logger/state/loggerDraftTypes";
import { slotDraftToEntry } from "../../Logger/slotDraftHydration";
import { useExerciseNameBySlug } from "../../Logger/exerciseName";

export type PrescribeReadAdapterArgs = {
  /** The composer's own live Draft — the ONE source of truth. */
  programDraft: Draft;
  /** The day×week cursor, already clamped by the caller. */
  selectedDay: number;
  selectedWeek: number;
  /** The FULL exercise catalog (ProgramComposerProps.exerciseCatalog). */
  exerciseCatalog: ReadonlyArray<Exercise> | undefined;
  /** The unit the DRAFT cells were seeded in — see PrescribeMountArgs.cellUnit
   *  for why it is never the athlete's display preference. */
  cellUnit: UnitsPreference;
  profile: LoggerProfile;
};

/** PE-22 K1 — the READ half of the prescribe mount: the composer's program
 *  Draft, seen as the Logger card sees it.
 *
 *  READ ONLY: nothing here writes the program Draft. The card's edits go the
 *  other way, through `applyLoggerAction` (prescribeDispatch) and the K3
 *  command hooks. Extracted from usePrescribeMount at parity when that file
 *  reached 492/500 (CN-prescribe-mount-split) — the same house-move the
 *  K2.3/K3.2/K3.4 concerns already made into this folder
 *  (usePrescribePlacement, usePrescribeSetCommands).
 *
 *  ⚠️ ONE deliberate exception to "read only", and it is why `setCatalog`
 *  is in the return: the catalog MIRROR is writable state, because
 *  useTimerDefaults writes an optimistic PATCH result back into it (exactly
 *  as useLoggerCatalog does in the real Logger). The mirror travelled with
 *  the projection before this split and still does — splitting them would
 *  put the state and its only reader in two files for no gain.
 *
 *  ⛔ It wraps `slotDraftToEntry`, it does not re-implement it: that shared
 *  per-slot transform is also what prescribeRoster reads, and two answers to
 *  "what does this slot look like at week N" is the thing that diverges.
 */
export function usePrescribeReadAdapter({
  programDraft,
  selectedDay,
  selectedWeek,
  exerciseCatalog,
  cellUnit,
  profile,
}: PrescribeReadAdapterArgs) {
  // The composer only carries CatalogItem[] (slug+name); the card needs the
  // FULL Exercise[] row, which is exerciseCatalog. Kept as local state (not a
  // raw prop pass-through) because timerDefaults writes an optimistic PATCH
  // result back into it, exactly like useLoggerCatalog.
  const [catalog, setCatalog] = useState<Exercise[] | null>(() =>
    exerciseCatalog ? [...exerciseCatalog] : null,
  );
  // Re-seed when the host hands us a fresh catalog reference (the host's own
  // fetch/refresh cadence) — timerDefaults' optimistic write still wins
  // between refreshes, same as the real Logger's catalog mirror.
  useEffect(() => {
    if (exerciseCatalog) setCatalog([...exerciseCatalog]);
  }, [exerciseCatalog]);

  const getExerciseNameBySlug = useExerciseNameBySlug(catalog);

  // A prescription has no session, so there is nothing real to timestamp —
  // but LoggerDraft.startedAt is required by the type, and every reader of
  // it (useSetCyclePill's 'log-past' branch) is mode-gated off in prescribe.
  // One mount-time ISO string, never re-read for meaning.
  const [startedAt] = useState(() => new Date().toISOString());

  // The card renders in the athlete's OWN preference (LoggerProfile), same
  // source useLoggerScreen's `defaultUnit` reads — NOT `cellUnit`.
  const defaultUnit = profile.unitsPreference;
  const displayUnit: WeightUnit = defaultUnit === "lb" ? "lb" : "kg";

  // The selected session's slots, at the selected week, as the card's
  // DraftEntry[]. Filters the `null`s (inactive weeks).
  const draft: LoggerDraft = useMemo(() => {
    const session = programDraft.sessions[selectedDay];
    const slots = session?.slots ?? [];
    const entries: DraftEntry[] = [];
    for (const slot of slots) {
      const exerciseRow = catalog?.find((c) => c.slug === slot.exerciseId);
      const entry = slotDraftToEntry(slot, selectedWeek, {
        exercise: exerciseRow
          ? {
              name: getExerciseNameBySlug(exerciseRow.slug, slot.exerciseName),
              isBodyweight: exerciseRow.isBodyweight,
              movementPattern: exerciseRow.movementPattern,
              isTimeBased: exerciseRow.isTimeBased,
              unilateral: exerciseRow.unilateral,
            }
          : undefined,
        cellUnit,
        displayUnit,
      });
      if (entry) entries.push(entry);
    }
    return { startedAt, entries };
  }, [
    programDraft,
    selectedDay,
    selectedWeek,
    catalog,
    cellUnit,
    displayUnit,
    startedAt,
    getExerciseNameBySlug,
  ]);

  return {
    draft,
    catalog,
    setCatalog,
    getExerciseNameBySlug,
    defaultUnit,
    displayUnit,
  };
}
